import React, { Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectAppVisible } from "reducers/auth";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { SentryRoutes } from "./sentryConfig";
import { refreshAccessToken } from "api";
import ErrorBoundary from "components/ErrorBoundary";
import { useCheckSignedIn } from "helpers/useCheckSignedIn";
import ErrorFallback from "components/fallbacks/ErrorFallback";
import Page from "components/Page";
import { Header } from "components/navigation/Header";
import PlatformUsers from "pages/PlatformUsers";
import PrincipleUsers from "pages/PrincipleUsers";
import StaticContents from "pages/staticContents";
import Navigation from "components/navigation/Navigation";
import { useSignOutAcrossTabs } from "helpers/useSignOutAcrossTabs";
import { Toasts } from "components/Toasts";
import { registerUserLocale } from "helpers/userLocale";
import { IdleSessionHandler } from "helpers/IdleSessionHandler";
import { Spinner } from "components/Spinner/Spinner";
import { APP_ROUTES } from "config";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import Account from "pages/Account";
import { useLoadScopes } from "helpers/auth";
import { isShopOnlyUser } from "helpers/shopOnlyUser";
import { PublicHeader } from "components/navigation/PublicHeader";
import { Footer } from "components/Footer/Footer";
import Settings from "pages/Settings";
import PermissionsErrorFallback from "components/fallbacks/PermissionsErrorFallback";
import Companies from "pages/Companies";
import BrandSettings from "components/BrandSettings";
import Sites from "pages/Sites";
import Projects from "pages/Projects";
import Roles from "./pages/Roles";
import Contacts from "pages/Contacts";
import Documents from "pages/Documents";
import Catalogues from "pages/Catalogues";
import Dashboards from "pages/dashboards";
import ClientCompanies from "pages/ClientCompanies";
import SiteCustomFields from "pages/CustomFields/SiteCustomFields";
import ProjectCustomFields from "pages/CustomFields/ProjectCustomFields";
import AssetCustomFields from "pages/CustomFields/AssetCustomFields";
import Programmes from "pages/Programmes";
import Gateways from "pages/Gateways";
import SiteCustomTypes from "pages/CustomTypes/SiteCustomTypes";
import ProjectCustomTypes from "pages/CustomTypes/ProjectCustomTypes";
import ProjectStatusCustomTypes from "pages/CustomTypes/ProjectStatusCustomTypes";
import SiteStatusCustomTypes from "pages/CustomTypes/SiteStatusCustomTypes";
import ContactCustomTypes from "pages/CustomTypes/ContactCustomTypes";
import Assets from "pages/Assets";
import AssetCustomTypes from "pages/CustomTypes/AssetCustomTypes";
import RoleCustomTypes from "pages/CustomTypes/RolesCustomTypes";
import ProgrammeCustomTypes from "pages/CustomTypes/ProgrammeCustomTypes";
import ProjectManagement from "pages/ProjectManagement";
import SnaggingAssets from "pages/SnaggingAssets/SnaggingAssets";
import DocumentsCustomTypes from "pages/CustomTypes/DocumentsCustomTypes";
import Currencies from "pages/Currencies";
import InvoiceCategoryTypes from "pages/InvoiceCategoryTypes";
import PaymentOptionTypes from "pages/PaymentOptionTypes";
import ExchangeRates from "pages/ExchangeRates";
import UserPools from "pages/UserPools";
import Holidays from "pages/Holidays";
import WorkflowConfigurations from "pages/WorkflowConfiguration";
import TaskWorkflowSyncMappings from "pages/TaskWorkflowSyncMappings";
import Surveys from "pages/Surveys";
import HomeDashboard from "pages/homes";
import WorkflowNotifications from "pages/workflowNotifications";
import Workflows from "pages/Workflows";
import ReasonCodes from "pages/ReasonCodes";
import CatalogueGroupCustomTypes from "pages/CustomTypes/CatalogueGroupCustomTypes";
import CatalogueSubGroupCustomTypes from "pages/CustomTypes/CatalogueSubGroupCustomTypes";
import CatalogueCategoryCustomTypes from "pages/CustomTypes/CatalogueCategoryCustomTypes";
import Invoices from "pages/Invoices";
import OutboundLogs from "pages/OutboundLogs";
import InboundLogs from "pages/InboundLogs";
import CatalogueItemCustomFields from "pages/CustomFields/CatalogueItemCustomFields";
import SurveyTemplates from "pages/SurveyTemplates";
import Surveyors from "pages/Surveyors";
import EmailTemplates from "pages/EmailTemplates";
import KPIs from "pages/KPIs";
import CommunicationCustomTypes from "pages/CustomTypes/CommunicationCustomTypes";
import CommunicationCategoryCustomTypes from "pages/CustomTypes/CommunicationCategoryCustomTypes";
import { HomeDashboardContainer } from "pages/homes/HomeDashboard";
import SnaggingCategoryCustomTypes from "pages/CustomTypes/SnaggingCategoryCustomTypes";
import Shop from "pages/Shop";
import { CartProvider } from "components/ShoppingCart/CartContext";
import Redirect from "components/Redirect/Redirect";
import SupplierFeedback from "pages/SupplierFeedback";
import NCRCategory from "pages/NCRCategory";
import Countries from "pages/Countries";

/** Standard Components */
const SignIn = lazyWithRetry(() => import("components/SignIn/SignIn"));
const SignUp = lazyWithRetry(() => import("components/SignUp/SignUp"));
const AuthRedirect = lazyWithRetry(() => import("components/AuthRedirect/AuthRedirect"));
const ForgottenPassword = lazyWithRetry(
  () => import("components/ForgottenPassword/ForgottenPassword")
);
const ResetPassword = lazyWithRetry(() => import("components/ResetPassword/ResetPassword"));

const AccountVerification = lazyWithRetry(
  () => import("components/AccountVerification/AccountVerification")
);
const NotFound = lazyWithRetry(() => import("pages/NotFound"));
const Home = lazyWithRetry(() => import("pages/Home"));

const NotFoundPage = () => (
  <Page isPublic>
    <NotFound />
  </Page>
);

const AccountVerificationPage = () => (
  <Page isPublic>
    <AccountVerification />
  </Page>
);

const ResetPasswordPage = () => {
  const location = useLocation();
  return (
    <Page isPublic>
      <ResetPassword location={location} />
    </Page>
  );
};

const HomePage = () => (
  <Page>
    <Home />
  </Page>
);

const ForgottenPasswordPage = () => (
  <Page isPublic>
    <ForgottenPassword />
  </Page>
);

const SignUpPage = () => (
  <Page isPublic>
    <SignUp />
  </Page>
);

// Array of strings represeting the paths where nav should be hidden.
const HIDE_NAV_ON_PATHS: string[] = [];
const HIDE_PUBLIC_NAV_ON_PATHS: string[] = [];
const HIDE_FOOTER_ON_PATHS: string[] = [];

export type HeaderAction = "Notification" | "Message" | "Account";

/* Main App */
const App: React.FC = () => {
  registerUserLocale();

  const [mobileNavOpen, setMobileNavOpen] = React.useState<boolean>(false);
  const [shrinkNavMenu, setShrinkNavMenu] = React.useState<boolean>(false);
  const isSignedIn = useCheckSignedIn();
  const isAppVisible = useSelector(selectAppVisible);
  const scopes = useLoadScopes();

  // Check if user is shop-only (has shop access but no home access)
  const userIsShopOnly = React.useMemo(() => {
    return isShopOnlyUser(scopes, isSignedIn);
  }, [isSignedIn, scopes]);

  // Set shrink nav menu to true for shop-only users
  useEffect(() => {
    if (userIsShopOnly) {
      setShrinkNavMenu(true);
    }
  }, [userIsShopOnly]);

  //IXSTD-5436 Clear lingering signOutAlert flag on app initialization
  useEffect(() => {
    if (localStorage.getItem("signOutAlert") != null) {
      localStorage.removeItem("signOutAlert");
    }
  }, []);

  const [testRedirected, setTestRedirected] = React.useState<boolean | null>();

  useEffect(() => {
    (async () => {
      if (window) {
        if (!window.localStorage.getItem("mfaAutoSignOut")) {
          await refreshAccessToken().catch(() => console.log("Failed to refresh access token"));
        }
      } else {
        await refreshAccessToken().catch(() => console.log("Failed to refresh access token"));
      }
    })();
  }, []);

  const location = useLocation();
  const pathname = location.pathname;
  const currentLocationIncludes = (locationStrings: string[]): boolean => {
    if (pathname) {
      for (let i = 0; i < locationStrings.length; i++) {
        const current = locationStrings[i];
        if (pathname.includes(current)) {
          return true;
        }
      }
    }

    return false;
  };
  const navHidden = !isSignedIn || currentLocationIncludes(HIDE_NAV_ON_PATHS);
  const publicNavHidden = isSignedIn || currentLocationIncludes(HIDE_PUBLIC_NAV_ON_PATHS);
  const footerHidden = isSignedIn || currentLocationIncludes(HIDE_FOOTER_ON_PATHS);

  const [headerActions, setHeaderActions] = React.useState<HeaderAction | null>(null);

  // If we sign out here, we should sign out in other open windows.
  useSignOutAcrossTabs();

  // At startup, we check to see if we should display a signin page or the content.
  // Return nothing, so that we don't flash a view of signin if unneccessary.
  if (!isAppVisible) return null;

  const openMobileNav = () => {
    setMobileNavOpen(true);
  };

  const handleShrinkNavMenu = () => {
    setShrinkNavMenu(!shrinkNavMenu);
  };

  return (
    <ErrorBoundary fallback={<ErrorFallback />} permissionsFallback={<PermissionsErrorFallback />}>
      <CartProvider>
        <IdleSessionHandler />
        <Suspense fallback={<Spinner positionAbsolute />}>
          <Navigation
            location={location}
            closeMobileNav={() => setMobileNavOpen(false)}
            mobileNavOpen={mobileNavOpen}
            hide={navHidden}
            shrinkNavMenu={shrinkNavMenu}
          />
          <div
            className={`${navHidden ? "Main NoNav" : "Main"} ${shrinkNavMenu ? "SmallNav" : ""} ${
              mobileNavOpen ? "MobileNavOpen" : ""
            }`}
          >
            <BrandSettings />

            <Header
              hideHeader={navHidden}
              handleShrinkNavMenu={handleShrinkNavMenu}
              openMobileNav={openMobileNav}
              setHeaderActions={setHeaderActions}
              headerActions={headerActions}
            />
            <PublicHeader hideHeader={publicNavHidden} />
            <SentryRoutes>
              <Route path={APP_ROUTES.redirect} element={<Redirect />} />
              <Route path={APP_ROUTES.home} element={<HomePage />} />
              <Route path={APP_ROUTES.platformUsers} element={<PlatformUsers />} />
              <Route path={APP_ROUTES.principleUsers} element={<PrincipleUsers />} />
              <Route path={APP_ROUTES.companies} element={<Companies />} />
              <Route path={APP_ROUTES.clientCompanies} element={<ClientCompanies />} />
              <Route path={APP_ROUTES.sites} element={<Sites />} />
              <Route path={APP_ROUTES.roles} element={<Roles />} />
              <Route path={APP_ROUTES.projects} element={<Projects />} />
              <Route
                path={APP_ROUTES.contacts}
                element={
                  <Contacts setTestRedirected={setTestRedirected} testRedirected={testRedirected} />
                }
              />
              <Route path={APP_ROUTES.documents} element={<Documents />} />
              <Route path={APP_ROUTES.staticContents} element={<StaticContents />} />
              <Route path={APP_ROUTES.signIn} element={<SignIn />} />
              <Route path={APP_ROUTES.signUp} element={<SignUpPage />} />
              <Route path={APP_ROUTES.authRedirect} element={<AuthRedirect />} />
              <Route path={APP_ROUTES.forgotPassword} element={<ForgottenPasswordPage />} />
              <Route path={APP_ROUTES.resetPassword} element={<ResetPasswordPage />} />
              <Route path={APP_ROUTES.accountVerification} element={<AccountVerificationPage />} />
              <Route path={APP_ROUTES.account} element={<Account />} />
              <Route path={APP_ROUTES.settings} element={<Settings />} />
              <Route path={APP_ROUTES.cataloguesitems} element={<Catalogues />} />
              <Route path={APP_ROUTES.assets} element={<Assets />} />
              <Route path={APP_ROUTES.programmes} element={<Programmes />} />
              <Route path={APP_ROUTES.sitecustomfields} element={<SiteCustomFields />} />
              <Route path={APP_ROUTES.projectcustomfields} element={<ProjectCustomFields />} />
              <Route path={APP_ROUTES.assetcustomfields} element={<AssetCustomFields />} />
              <Route
                path={APP_ROUTES.catalogueitemcustomfields}
                element={<CatalogueItemCustomFields />}
              />
              <Route path={APP_ROUTES.sitecustomtypes} element={<SiteCustomTypes />} />
              <Route path={APP_ROUTES.assetcustomtypes} element={<AssetCustomTypes />} />
              <Route path={APP_ROUTES.programmecustomtypes} element={<ProgrammeCustomTypes />} />
              <Route path={APP_ROUTES.rolecustomtypes} element={<RoleCustomTypes />} />
              <Route path={APP_ROUTES.projectcustomtypes} element={<ProjectCustomTypes />} />
              <Route
                path={APP_ROUTES.projectstatuscustomtypes}
                element={<ProjectStatusCustomTypes />}
              />
              <Route path={APP_ROUTES.gateways} element={<Gateways />} />
              <Route path={APP_ROUTES.workflowconfiguration} element={<WorkflowConfigurations />} />
              <Route path={APP_ROUTES.taskworkflowsyncmappings} element={<TaskWorkflowSyncMappings />} />
              <Route path={APP_ROUTES.dashboards} element={<Dashboards />} />
              <Route path={APP_ROUTES.programmes} element={<Programmes />} />
              <Route path={APP_ROUTES.sitestatuscustomtypes} element={<SiteStatusCustomTypes />} />
              <Route path={APP_ROUTES.sitecustomtypes} element={<SiteCustomTypes />} />
              <Route path={APP_ROUTES.contactcustomtypes} element={<ContactCustomTypes />} />
              <Route path={APP_ROUTES.projectmanagement} element={<ProjectManagement />} />
              <Route path={APP_ROUTES.snaggingassets} element={<SnaggingAssets />} />
              <Route path={APP_ROUTES.currencies} element={<Currencies />} />
              <Route path={APP_ROUTES.invoicecategorytypes} element={<InvoiceCategoryTypes />} />
              <Route path={APP_ROUTES.paymentoptiontypes} element={<PaymentOptionTypes />} />
              <Route path={APP_ROUTES.exchangerates} element={<ExchangeRates />} />
              <Route path={APP_ROUTES.projectmanagement} element={<ProjectManagement />} />
              <Route path={APP_ROUTES.invoicecategorytypes} element={<InvoiceCategoryTypes />} />
              <Route path={APP_ROUTES.paymentoptiontypes} element={<PaymentOptionTypes />} />
              <Route path={APP_ROUTES.exchangerates} element={<ExchangeRates />} />
              <Route path={APP_ROUTES.userpools} element={<UserPools />} />
              <Route path={APP_ROUTES.holidays} element={<Holidays />} />
              <Route path="*" element={<NotFoundPage />} />
              <Route path={APP_ROUTES.documentcustomtypes} element={<DocumentsCustomTypes />} />
              <Route path={APP_ROUTES.surveys} element={<Surveys />} />
              <Route path={APP_ROUTES.reasoncodes} element={<ReasonCodes />} />
              <Route path={APP_ROUTES.workflows} element={<Workflows />} />
              <Route path={APP_ROUTES.homedashboard} element={<HomeDashboardContainer />} />
              <Route path={APP_ROUTES.workflowNotifications} element={<WorkflowNotifications />} />
              <Route path={APP_ROUTES.cataloguegroups} element={<CatalogueGroupCustomTypes />} />
              <Route
                path={APP_ROUTES.cataloguesubgroups}
                element={<CatalogueSubGroupCustomTypes />}
              />
              <Route
                path={APP_ROUTES.cataloguecategories}
                element={<CatalogueCategoryCustomTypes />}
              />
              <Route path={APP_ROUTES.invoices} element={<Invoices />} />
              <Route path={APP_ROUTES.greendoorpush} element={<OutboundLogs />} />
              <Route path={APP_ROUTES.greendoorapi} element={<InboundLogs />} />
              <Route path={APP_ROUTES.surveytemplates} element={<SurveyTemplates />} />
              <Route path={APP_ROUTES.surveyors} element={<Surveyors />} />
              <Route path={APP_ROUTES.emailtemplates} element={<EmailTemplates />} />
              <Route path={APP_ROUTES.kpis} element={<KPIs />} />
              <Route
                path={APP_ROUTES.communicationcustomtypes}
                element={<CommunicationCustomTypes />}
              />
              <Route
                path={APP_ROUTES.communicationcategories}
                element={<CommunicationCategoryCustomTypes />}
              />
              <Route
                path={APP_ROUTES.snaggingcategories}
                element={<SnaggingCategoryCustomTypes />}
              />
              <Route path={APP_ROUTES.shop} element={<Shop />} />
              <Route path={APP_ROUTES.supplierfeedback} element={<SupplierFeedback />} />
              <Route path={APP_ROUTES.ncrcategory} element={<NCRCategory />} />
              <Route path={APP_ROUTES.countries} element={<Countries />} />
            </SentryRoutes>
            <Footer hideFooter={footerHidden} />
          </div>
        </Suspense>

        <Toasts />
      </CartProvider>
    </ErrorBoundary>
  );
};

export default App;

export const homePermissionsRequested = [
  "actions-approval-list",
  "notifications-list",
  "notifications-seen",
  "notifications-view",
  "review-action",
  "review-approval",
  "list-lastviewedprojects",
];
export const allPermissions = homePermissionsRequested; //permissionsRequested.concat(homePermissionsRequested);
